import { DND_5E_CORE_ID } from "@/lib/constants";
import {
  type AccountCharacterDetail,
  type CreateAccountCharacterDnd5eResult,
  CreateAccountCharacterDnd5eResultSchema,
} from "@/lib/core/schemas/account-character";
import {
  type CharacterCreationDraftDnd5e,
  CharacterCreationDraftDnd5eSchema,
  type CharacterCreationEvaluationDnd5e,
  CharacterCreationEvaluationDnd5eSchema,
  type CommitCharacterCreationDnd5eResult,
  CommitCharacterCreationDnd5eResultSchema,
} from "@/lib/core/schemas/character-creation";

export async function evaluateCharacterCreation(
  campaignId: string,
  draft: CharacterCreationDraftDnd5e
): Promise<CharacterCreationEvaluationDnd5e> {
  const res = await fetch(
    `/api/campaigns/${encodeURIComponent(campaignId)}/character-creation/evaluate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(CharacterCreationDraftDnd5eSchema.parse(draft)),
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Network error" }));
    throw new Error(body.error ?? "Character creation evaluation failed");
  }

  return CharacterCreationEvaluationDnd5eSchema.parse(await res.json());
}

export async function evaluateStandaloneCharacterCreation(
  draft: CharacterCreationDraftDnd5e
): Promise<CharacterCreationEvaluationDnd5e> {
  const res = await fetch("/api/account/character-creation/evaluate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      rulesetId: DND_5E_CORE_ID,
      draft: CharacterCreationDraftDnd5eSchema.parse(draft),
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Network error" }));
    throw new Error(
      body.error ?? "Standalone character creation evaluation failed"
    );
  }

  return CharacterCreationEvaluationDnd5eSchema.parse(await res.json());
}

export async function commitCharacterCreation(
  campaignId: string,
  draft: CharacterCreationDraftDnd5e,
  expectedPlanDigest: string
): Promise<CommitCharacterCreationDnd5eResult> {
  const res = await fetch(
    `/api/campaigns/${encodeURIComponent(campaignId)}/character-creation/commit`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        draft: CharacterCreationDraftDnd5eSchema.parse(draft),
        expectedPlanDigest,
      }),
    }
  );

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Network error" }));
    const variant =
      typeof body?.data?.variant === "string" ? ` (${body.data.variant})` : "";
    throw new Error(
      typeof body.error === "string"
        ? `${body.error}${variant}`
        : `Character creation commit failed${variant}`
    );
  }

  return CommitCharacterCreationDnd5eResultSchema.parse(await res.json());
}

export async function createAccountCharacter(
  draft: CharacterCreationDraftDnd5e,
  expectedPlanDigest: string
): Promise<CreateAccountCharacterDnd5eResult> {
  const res = await fetch("/api/account/character-creation/confirm", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      rulesetId: DND_5E_CORE_ID,
      draft: CharacterCreationDraftDnd5eSchema.parse(draft),
      expectedPlanDigest,
    }),
  });

  if (!res.ok) {
    const body = await res.json().catch(() => ({ error: "Network error" }));
    const variant =
      typeof body?.data?.variant === "string" ? ` (${body.data.variant})` : "";
    throw new Error(
      typeof body.error === "string"
        ? `${body.error}${variant}`
        : `Account character creation failed${variant}`
    );
  }

  return CreateAccountCharacterDnd5eResultSchema.parse(await res.json());
}

export async function fetchAccountCharacters(): Promise<
  readonly AccountCharacterDetail[]
> {
  const res = await fetch("/api/account/characters");
  if (!res.ok) {
    throw new Error("Account characters fetch failed");
  }
  return (await res.json()) as readonly AccountCharacterDetail[];
}
